import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { useSetState } from '@domain-group/react-hooks'
import {
  Box, Button, DashboardModule, Form, Text,
} from '@domain-group/suit-theme'

import useAgencyProviders from '@hooks/useAgencyProviders'
import useProviderPriceList from '@hooks/useProviderPriceList'

import handleCallbackAction from '@functions/handleCallbackAction'

import ToggleListItem from '@components/ToggleListItem'

import type { ProviderPriceListModel } from '@models/types'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'

const defaultState = {
  direction: '',
  percentage: '',
  roundToNearestDollar: true,
}

interface ProviderPriceListDetailsProps {
  providerPriceList: Partial<ProviderPriceListModel>,
}

const AdjustPricesByPercentage = (props: ProviderPriceListDetailsProps) => {
  const {
    providerPriceList,
  } = props

  const providerPriceListPayload = useProviderPriceList(providerPriceList)
  const {
    callbacks: {
      adjustPriceByPercentage,
    },
    updating,
  } = providerPriceListPayload

  const agencyProviderPayload = useAgencyProviders({
    filters: {
      provider_price_list_id: providerPriceList?.id,
    },
    performHttpRequests: !!providerPriceList?.id,
  })

  const { hasFilteredAgencyProviders } = agencyProviderPayload

  const [state, setState] = useSetState(defaultState)
  const { direction, percentage, roundToNearestDollar } = state

  const adjustPriceParams = () => {
    const percentageInt = Number(percentage)

    switch (direction){
      case 'increase':
        return {
          percentage: 100 + percentageInt,
          undo_change: false,
          round_to_nearest_dollar: roundToNearestDollar,
        }
      case 'decrease':
        return {
          percentage: 100 - percentageInt,
          undo_change: false,
          round_to_nearest_dollar: roundToNearestDollar,
        }
      case 'undoIncrease':
        return {
          percentage: 100 + percentageInt,
          undo_change: true,
          round_to_nearest_dollar: false,
        }
      case 'undoDecrease':
        return {
          percentage: 100 - percentageInt,
          undo_change: true,
          round_to_nearest_dollar: false,
        }
      default:
        return {
          percentage: 100,
          undo_change: false,
          round_to_nearest_dollar: false,
        }
    }
  }

  const adjustPricePayload = {
    callbacks: {
      action: adjustPriceByPercentage,
    },
    entityParams: adjustPriceParams(),
    requestOptions: defaultRequestOptions.providerPriceList,
    toastText: 'Prices Updated',
  }

  const defaultOptions = [
    { value: '', label: 'Please Select...' },
    { value: 'increase', label: 'Increase by' },
    { value: 'decrease', label: 'Decrease by' },
  ]

  const options = [...defaultOptions,
    { value: 'undoIncrease', label: 'Undo Increase of' },
    { value: 'undoDecrease', label: 'Undo Decrease of' },
  ]

  const selectOptions = roundToNearestDollar ? defaultOptions : options

  return (
    <DashboardModule title="Adjust Prices by Percentage" contentBoxProps={{ flexDirection: 'column' }}>
      <Box marginBottom="medium" borderBottom="1px solid" borderColor="lightestGrey">
        <Box flexDirection={['column', 'row']} padding="large">
          <Box justifyContent="center" alignItems="center" fontSize="xlarge" marginRight="large" width="auto">
            <FontAwesomeIcon icon={faExclamationTriangle} />
          </Box>

          <Box>
            <Text color="bodyFontLightColor" fontSize="xsmall" lineHeight="1.3">
              Bulk price adjustments can only be done on price lists that are not assigned to agencies.
              Best practice is to create a duplicate or fork of this price list and adjust the prices then bulk
              reassign the agencies or set the new price list as the next price list and set the expiry date of
              the current price list.
            </Text>
          </Box>
        </Box>
      </Box>

      <Form padding="large">
        <Form.Row columns="repeat(1, 1fr)">
          <ToggleListItem
            onClick={() => setState({ roundToNearestDollar: !roundToNearestDollar })}
            toggleValue={roundToNearestDollar}
          >
            <ToggleListItem.Title>Round to nearest dollar</ToggleListItem.Title>

            <ToggleListItem.Description>
              This action will round prices to the nearest dollar. Please note that once applied,
              this change cannot be undone.
            </ToggleListItem.Description>
          </ToggleListItem>
        </Form.Row>

        <Form.Row>
          <Form.Field label="Adjust">
            <select
              data-value-type="text"
              name="direction"
              value={direction}
              onChange={e => setState({ direction: e.target.value })}
              disabled={hasFilteredAgencyProviders}
            >
              {selectOptions.map(option => (
                <option value={option.value}> {option.label} </option>
              ))}
            </select>
          </Form.Field>

          {direction && (
            <Form.Field label="Percentage">
              <input
                name="percentage"
                value={percentage}
                type="number"
                onChange={e => setState({ percentage: e.target.value })}
              />
            </Form.Field>
          )}
        </Form.Row>

      </Form>

      <DashboardModule.Footer>
        <DashboardModule.FooterRight>
          <Button
            buttonStyle="primaryCreate"
            disabled={!direction || !percentage}
            loading={updating}
            style={{ maxWidth: 123 }}
            // @ts-expect-error TS2345
            onClick={() => handleCallbackAction(adjustPricePayload)}
            size="medium"
          >
            Adjust Prices
          </Button>
        </DashboardModule.FooterRight>
      </DashboardModule.Footer>
    </DashboardModule>
  )
}

export default AdjustPricesByPercentage
